import {
  DownOutlined,
  LogoutOutlined,
  SettingOutlined,
  UserOutlined
} from "@ant-design/icons"
import {
  App,
  Avatar,
  Button,
  Dropdown,
  Flex,
  type MenuProps,
  Typography
} from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate } from "react-router"
import { useLocation } from "react-router-dom"
import { type NavigateFunction } from "react-router/dist/lib/hooks"

import { RoutesPaths } from "../../constants/routes.ts"
import { SETTINGS_GUIDE_TARGET } from "../Sidebar/sidebar-config.tsx"
import type { AppDispatch, RootState } from "../../store.ts"
import { logout } from "../LoginPage/redux/auth.thunks.ts"
import { fetchUserInfo } from "./redux/user.thunks"

const { Text } = Typography

const HeaderUserMenu = ({
  user,
  logout,
  getUserInfo
}: ConnectedProps<typeof connector>) => {
  const { t } = useTranslation()
  const { modal } = App.useApp()
  const location = useLocation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const role = user?.role?.name

  const name = useMemo(() => {
    const fullName = [user?.first_name, user?.middle_name, user?.last_name]
      ?.filter(Boolean)
      ?.join(" ")

    return fullName || t("userMenu.noName")
  }, [user, t])

  const handleLogout = () => {
    setOpen(false)
    modal.confirm({
      title: t("logout.confirmTitle"),
      content: t("logout.confirmBody"),
      okText: t("logout.confirmOk"),
      cancelText: t("logout.confirmCancel"),
      onOk: () => {
        logout(navigate)
      }
    })
  }

  const openAndClose = (path: string) => {
    setOpen(false)
    navigate(path)
  }

  useEffect(() => {
    if (!!user && !location?.pathname?.includes("account")) {
      getUserInfo()
    }
  }, [])

  const avatar = (
    <Avatar
      size={32}
      className="shrink-0 bg-[#EAF2F3] text-[#2A7D8C]"
      src={user?.avatar}
      icon={!user?.avatar ? <UserOutlined /> : undefined}
    />
  )

  // AntD's own menu API rather than a hand-rolled `dropdownRender` panel: a
  // custom panel is portaled to the end of the body, so Tab from the trigger
  // walked straight past it and My profile / Log out were unreachable by
  // keyboard. `menu` hands focus, arrow keys and focus-return to rc-menu.
  //
  // This menu is the only way into Settings — neither the rail nor the header
  // offers it any more. My profile and Settings land on the same `/settings`,
  // because that page's index route IS Profile & User; which tabs then open is
  // decided by the route gates, untouched by this menu.
  const items: MenuProps["items"] = [
    {
      key: "identity",
      type: "group",
      label: (
        <Flex gap={10} align="center" className="overflow-hidden py-1.5">
          {avatar}
          <Flex vertical className="overflow-hidden leading-tight">
            <Text ellipsis strong title={name}>
              {name}
            </Text>
            {role && (
              <Text ellipsis className="text-xs" type="secondary" title={role}>
                {role}
              </Text>
            )}
          </Flex>
        </Flex>
      )
    },
    { type: "divider" },
    {
      key: "profile",
      icon: <UserOutlined />,
      label: t("userMenu.myProfile")
    },
    {
      key: "settings",
      icon: <SettingOutlined />,
      label: t("userMenu.settings")
    },
    { type: "divider" },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: t("userMenu.logout"),
      danger: true
    }
  ]

  const onMenuClick: MenuProps["onClick"] = ({ key }) => {
    if (key === "profile" || key === "settings") {
      openAndClose(RoutesPaths.settings.profile)
    }
    if (key === "logout") handleLogout()
  }

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={["click"]}
      // Without this the menu opens but focus stays on the trigger, so the
      // rows are reachable only with a pointer.
      autoFocus
      placement="bottomRight"
      arrow={false}
      overlayClassName="ezb-user-menu"
      menu={{ items, onClick: onMenuClick }}
    >
      <Button
        type="text"
        data-testid="header-user-menu"
        // The Company HQ product tour spotlights Settings here now that the
        // rail row and the header button are gone; the menu is closed when it
        // runs, so the trigger is the only element it can anchor to.
        data-guide-target={SETTINGS_GUIDE_TARGET}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("userMenu.label", { name })}
        className="flex items-center gap-2 px-1.5"
      >
        {avatar}
        <Text
          ellipsis
          className="hidden max-w-[9rem] text-sm font-medium xl:inline"
        >
          {name}
        </Text>
        <DownOutlined className="text-[10px] text-slate-400" />
      </Button>
    </Dropdown>
  )
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.userInfo
})

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getUserInfo: () => dispatch(fetchUserInfo()),
  logout: (navigate: NavigateFunction) => dispatch(logout({ navigate }))
})

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(HeaderUserMenu)
